import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import GroupWorkOutlinedIcon from "@mui/icons-material/GroupWorkOutlined";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { foyerApi } from "../http/foyer";
import { useCan } from "../auth/AuthContext";
import { tokens } from "../theme";
import { platformLabel } from "../utils/devicePresentation";
import { PageContainer, PageHeader } from "../components/PageHeader";
import HelpBadge from "../components/HelpBadge";
import type {
  DeviceGroup,
  DevicePlatform,
  FleetDevice,
} from "@shared/types/foyer";

function deviceLabel(d: FleetDevice): string {
  const where = [d.roomId, d.premiseName].filter(Boolean).join(" · ");
  return where || d.id;
}

function premiseKey(d: FleetDevice): string {
  return d.premiseName || d.premiseId || "Unassigned";
}

/** A small green/grey dot conveying live online status. */
function StatusDot({ online }: { online: boolean }) {
  return (
    <Box
      component="span"
      role="img"
      aria-label={online ? "Online" : "Offline"}
      sx={{
        width: 8,
        height: 8,
        borderRadius: "50%",
        flexShrink: 0,
        bgcolor: online ? tokens.success : tokens.inkMuted,
        opacity: online ? 1 : 0.45,
      }}
    />
  );
}

/** "{online}/{total} online" as a traffic-light tinted chip. */
function OnlineRollupChip({
  online,
  total,
}: {
  online: number;
  total: number;
}) {
  if (total === 0) {
    return (
      <Chip
        size="small"
        label="No devices"
        sx={{ bgcolor: tokens.rowAlt, color: tokens.inkMuted, fontWeight: 600 }}
      />
    );
  }
  const allOn = online === total;
  const noneOn = online === 0;
  const bg = allOn
    ? tokens.successTint
    : noneOn
      ? tokens.rowAlt
      : tokens.warningTint;
  const fg = allOn ? tokens.success : noneOn ? tokens.inkMuted : tokens.warning;
  return (
    <Chip
      size="small"
      label={`${online}/${total} online`}
      sx={{ bgcolor: bg, color: fg, fontWeight: 600 }}
    />
  );
}

/** The create/edit dialog: name, description, and a premise-grouped device picker. */
function GroupDialog({
  open,
  initial,
  devices,
  onClose,
  onSave,
}: {
  open: boolean;
  initial: DeviceGroup | null;
  devices: FleetDevice[];
  onClose: () => void;
  onSave: (v: {
    name: string;
    description: string;
    screenIds: string[];
  }) => void;
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [q, setQ] = useState("");
  const [plat, setPlat] = useState<DevicePlatform | "all">("all");

  useEffect(() => {
    if (!open) return;
    setName(initial?.name ?? "");
    setDescription(initial?.description ?? "");
    setSelected(new Set(initial?.screenIds ?? []));
    setQ("");
    setPlat("all");
  }, [open, initial]);

  // Platforms actually present in the fleet, for the quick-filter row.
  const platforms = useMemo(() => {
    const set = new Set<DevicePlatform>();
    for (const d of devices) if (d.platform) set.add(d.platform);
    return [...set].sort();
  }, [devices]);

  const filtered = useMemo(() => {
    const needle = q.toLowerCase();
    return devices.filter((d) => {
      if (plat !== "all" && d.platform !== plat) return false;
      if (!needle) return true;
      return [d.roomId, d.premiseName, d.model, d.id].some((v) =>
        v?.toLowerCase().includes(needle)
      );
    });
  }, [devices, q, plat]);

  // Group the visible devices by premise (sorted), so the picker reads as a
  // site map rather than a flat list.
  const groupedByPremise = useMemo(() => {
    const map = new Map<string, FleetDevice[]>();
    for (const d of filtered) {
      const key = premiseKey(d);
      const arr = map.get(key);
      if (arr) arr.push(d);
      else map.set(key, [d]);
    }
    return [...map.entries()]
      .map(([premise, list]) => ({
        premise,
        list: [...list].sort((a, b) =>
          deviceLabel(a).localeCompare(deviceLabel(b))
        ),
      }))
      .sort((a, b) => a.premise.localeCompare(b.premise));
  }, [filtered]);

  const filteredIds = useMemo(() => filtered.map((d) => d.id), [filtered]);
  const allFilteredSelected =
    filteredIds.length > 0 && filteredIds.every((id) => selected.has(id));
  const selectedOnline = useMemo(
    () =>
      devices.reduce((n, d) => n + (selected.has(d.id) && d.online ? 1 : 0), 0),
    [devices, selected]
  );

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function setMany(ids: string[], on: boolean) {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const id of ids) {
        if (on) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  }

  const filterLabel =
    plat === "all" ? "all shown" : `all ${platformLabel(plat)}`;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{initial ? "Edit group" : "New device group"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            autoFocus
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
          />
          <Box>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 1 }}
            >
              <Typography variant="subtitle2">Members</Typography>
              <Typography variant="caption" color="text.secondary">
                {selected.size} selected
                {selected.size > 0 ? ` · ${selectedOnline} online` : ""}
              </Typography>
            </Stack>

            <TextField
              size="small"
              fullWidth
              placeholder="Search devices…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              inputProps={{ "aria-label": "Search devices" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 1 }}
            />

            {platforms.length > 1 && (
              <Stack
                direction="row"
                spacing={1}
                sx={{ mb: 1, flexWrap: "wrap", gap: 1 }}
              >
                <Chip
                  size="small"
                  label="All"
                  variant={plat === "all" ? "filled" : "outlined"}
                  color={plat === "all" ? "primary" : "default"}
                  onClick={() => setPlat("all")}
                />
                {platforms.map((p) => (
                  <Chip
                    key={p}
                    size="small"
                    label={platformLabel(p)}
                    variant={plat === p ? "filled" : "outlined"}
                    color={plat === p ? "primary" : "default"}
                    onClick={() => setPlat(p)}
                  />
                ))}
              </Stack>
            )}

            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 0.5 }}
            >
              <Typography variant="caption" color="text.secondary">
                {filtered.length} device{filtered.length === 1 ? "" : "s"} shown
              </Typography>
              <Button
                size="small"
                disabled={filteredIds.length === 0}
                onClick={() => setMany(filteredIds, !allFilteredSelected)}
              >
                {allFilteredSelected ? "Clear" : `Select ${filterLabel}`}
              </Button>
            </Stack>

            <List
              dense
              subheader={<li />}
              sx={{
                maxHeight: 320,
                overflow: "auto",
                border: `1px solid ${tokens.rowAlt}`,
                borderRadius: 1,
                "& ul": { p: 0 },
              }}
            >
              {filtered.length === 0 && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ p: 2 }}
                >
                  No devices match.
                </Typography>
              )}
              {groupedByPremise.map(({ premise, list }) => {
                const ids = list.map((d) => d.id);
                const onlineCount = list.filter((d) => d.online).length;
                const selCount = ids.filter((id) => selected.has(id)).length;
                const allSel = selCount === ids.length;
                const someSel = selCount > 0 && !allSel;
                return (
                  <li key={premise}>
                    <ul>
                      <ListSubheader
                        disableSticky={false}
                        sx={{
                          bgcolor: tokens.surface,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          lineHeight: "40px",
                        }}
                      >
                        <Checkbox
                          edge="start"
                          size="small"
                          checked={allSel}
                          indeterminate={someSel}
                          onChange={(e) => setMany(ids, e.target.checked)}
                          inputProps={{
                            "aria-label": `Select all in ${premise}`,
                          }}
                          sx={{ p: 0.5 }}
                        />
                        <Typography
                          variant="subtitle2"
                          sx={{ flex: 1, minWidth: 0 }}
                          noWrap
                        >
                          {premise}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {onlineCount}/{ids.length} online
                        </Typography>
                      </ListSubheader>
                      {list.map((d) => (
                        <ListItemButton
                          key={d.id}
                          onClick={() => toggle(d.id)}
                          dense
                        >
                          <Checkbox
                            edge="start"
                            checked={selected.has(d.id)}
                            tabIndex={-1}
                            disableRipple
                            size="small"
                            inputProps={{ "aria-label": deviceLabel(d) }}
                          />
                          <StatusDot online={d.online} />
                          <ListItemText
                            sx={{ ml: 1 }}
                            primary={deviceLabel(d)}
                            secondary={
                              [platformLabel(d.platform), d.model]
                                .filter((v) => v && v !== "-")
                                .join(" · ") || undefined
                            }
                          />
                        </ListItemButton>
                      ))}
                    </ul>
                  </li>
                );
              })}
            </List>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!name.trim()}
          onClick={() =>
            onSave({
              name: name.trim(),
              description: description.trim(),
              screenIds: [...selected],
            })
          }
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** One group as a card, with online rollup, member chips and inline rename. */
function GroupCard({
  group,
  nameById,
  onlineById,
  canReboot,
  canMessage,
  onReboot,
  onMessage,
  onEdit,
  onDelete,
  onRename,
}: {
  group: DeviceGroup;
  nameById: Map<string, string>;
  onlineById: Map<string, boolean>;
  canReboot: boolean;
  canMessage: boolean;
  onReboot: () => void;
  onMessage: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onRename: (name: string) => void;
}) {
  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState(group.name);
  const inputRef = useRef<HTMLInputElement>(null);

  const total = group.screenIds.length;
  const online = group.screenIds.reduce(
    (n, id) => n + (onlineById.get(id) ? 1 : 0),
    0
  );
  const memberNames = group.screenIds.map((id) => nameById.get(id) ?? id);
  const shown = memberNames.slice(0, 6);
  const extra = memberNames.length - shown.length;

  function startRename() {
    setDraft(group.name);
    setRenaming(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  }
  function commit() {
    const next = draft.trim();
    setRenaming(false);
    if (next && next !== group.name) onRename(next);
  }

  return (
    <Card sx={{ p: 2, display: "flex", alignItems: "flex-start", gap: 2 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
          {renaming ? (
            <TextField
              inputRef={inputRef}
              size="small"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={commit}
              onKeyDown={(e) => {
                if (e.key === "Enter") commit();
                if (e.key === "Escape") setRenaming(false);
              }}
              inputProps={{ "aria-label": "Group name" }}
              sx={{ flex: 1 }}
            />
          ) : (
            <Tooltip title="Rename">
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  cursor: "text",
                  borderRadius: 1,
                  px: 0.5,
                  mx: -0.5,
                  "&:hover": { bgcolor: tokens.rowAlt },
                }}
                onClick={startRename}
              >
                {group.name}
              </Typography>
            </Tooltip>
          )}
          <OnlineRollupChip online={online} total={total} />
        </Stack>

        {group.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            {group.description}
          </Typography>
        )}

        {total === 0 ? (
          <Typography variant="body2" sx={{ color: tokens.warning }}>
            No devices yet - this group won’t match anything in a rollout.
          </Typography>
        ) : (
          <Stack
            direction="row"
            spacing={0.75}
            sx={{ flexWrap: "wrap", gap: 0.75, mt: 0.5 }}
          >
            {shown.map((label, i) => (
              <Chip
                key={`${group.screenIds[i]}-${i}`}
                size="small"
                variant="outlined"
                label={label}
                sx={{ maxWidth: 220 }}
              />
            ))}
            {extra > 0 && (
              <Chip
                size="small"
                label={`+${extra} more`}
                sx={{ bgcolor: tokens.rowAlt, color: tokens.inkMuted }}
              />
            )}
          </Stack>
        )}
      </Box>

      {renaming ? (
        <Stack direction="row">
          <IconButton aria-label="Save name" onMouseDown={commit}>
            <CheckIcon />
          </IconButton>
          <IconButton
            aria-label="Cancel rename"
            onMouseDown={() => setRenaming(false)}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      ) : (
        <Stack direction="row">
          {canReboot && (
            <Tooltip
              title={total === 0 ? "No devices to reboot" : "Reboot group"}
            >
              <span>
                <IconButton
                  aria-label="Reboot group"
                  disabled={total === 0}
                  onClick={onReboot}
                >
                  <RestartAltIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {canMessage && (
            <Tooltip
              title={total === 0 ? "No devices to message" : "Message group"}
            >
              <span>
                <IconButton
                  aria-label="Message group"
                  disabled={total === 0}
                  onClick={onMessage}
                >
                  <CampaignOutlinedIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
          <Tooltip title="Edit members">
            <IconButton aria-label="Edit group" onClick={onEdit}>
              <EditOutlinedIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete group">
            <IconButton aria-label="Delete group" onClick={onDelete}>
              <DeleteOutlineIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      )}
    </Card>
  );
}

/** Compose a reception message to push to every device in a group. */
function MessageDialog({
  group,
  onClose,
  onSend,
}: {
  group: DeviceGroup | null;
  onClose: () => void;
  onSend: (v: { title: string; body: string; durationMs: number }) => void;
}) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [seconds, setSeconds] = useState(8);

  useEffect(() => {
    if (group) {
      setTitle("");
      setBody("");
      setSeconds(8);
    }
  }, [group]);

  const total = group?.screenIds.length ?? 0;

  return (
    <Dialog open={!!group} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Message “{group?.name}”</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Shown on all {total} device{total === 1 ? "" : "s"} in this group.
          </Typography>
          <TextField
            label="Title (optional)"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            autoFocus
          />
          <TextField
            label="Message"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            inputProps={{ "aria-label": "Message body" }}
          />
          <TextField
            label="Duration (seconds)"
            type="number"
            value={seconds}
            onChange={(e) =>
              setSeconds(
                Math.max(1, Math.min(120, Number(e.target.value) || 1))
              )
            }
            sx={{ width: 180 }}
            inputProps={{ min: 1, max: 120 }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!body.trim()}
          onClick={() =>
            onSend({
              title: title.trim(),
              body: body.trim(),
              durationMs: seconds * 1000,
            })
          }
        >
          Send
        </Button>
      </DialogActions>
    </Dialog>
  );
}

type SortKey = "name" | "members" | "updated";

export default function DeviceGroupsPage() {
  const [groups, setGroups] = useState<DeviceGroup[]>([]);
  const [devices, setDevices] = useState<FleetDevice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<DeviceGroup | null>(null);
  const [messaging, setMessaging] = useState<DeviceGroup | null>(null);
  const [snack, setSnack] = useState<{
    severity: "success" | "error";
    text: string;
    withLink?: boolean;
  } | null>(null);
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<SortKey>("name");

  const canReboot = useCan("canManageDevices");
  const canMessage = useCan("canSendMessages");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [g, f] = await Promise.all([
        foyerApi.listDeviceGroups(),
        foyerApi.fleetDevices(),
      ]);
      setGroups(g.groups);
      setDevices(f.devices);
    } catch {
      setError("Failed to load device groups.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const nameById = useMemo(
    () => new Map(devices.map((d) => [d.id, deviceLabel(d)])),
    [devices]
  );
  const onlineById = useMemo(
    () => new Map(devices.map((d) => [d.id, d.online])),
    [devices]
  );

  const visibleGroups = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const list = groups.filter(
      (g) =>
        !needle ||
        g.name.toLowerCase().includes(needle) ||
        g.description?.toLowerCase().includes(needle)
    );
    const sorted = [...list];
    sorted.sort((a, b) => {
      if (sort === "members") return b.screenIds.length - a.screenIds.length;
      if (sort === "updated")
        return (b.updatedAt ?? "").localeCompare(a.updatedAt ?? "");
      return a.name.localeCompare(b.name);
    });
    return sorted;
  }, [groups, query, sort]);

  async function save(v: {
    name: string;
    description: string;
    screenIds: string[];
  }) {
    try {
      if (editing) {
        await foyerApi.updateDeviceGroup(editing.groupId, v);
      } else {
        await foyerApi.createDeviceGroup(v);
      }
      setDialogOpen(false);
      setEditing(null);
      await load();
    } catch {
      setError("Failed to save the group.");
    }
  }

  async function rename(g: DeviceGroup, name: string) {
    // Optimistic: reflect the new name immediately, reconcile on reload.
    setGroups((prev) =>
      prev.map((x) => (x.groupId === g.groupId ? { ...x, name } : x))
    );
    try {
      await foyerApi.updateDeviceGroup(g.groupId, { name });
    } catch {
      setError("Failed to rename the group.");
      await load();
    }
  }

  async function remove(g: DeviceGroup) {
    if (!window.confirm(`Delete group “${g.name}”?`)) return;
    try {
      await foyerApi.deleteDeviceGroup(g.groupId);
      await load();
    } catch {
      setError("Failed to delete the group.");
    }
  }

  async function reboot(g: DeviceGroup) {
    const n = g.screenIds.length;
    if (n === 0) return;
    if (
      !window.confirm(
        `Reboot all ${n} device${n === 1 ? "" : "s"} in “${g.name}”?`
      )
    )
      return;
    try {
      const res = await foyerApi.rebootGroup(g.groupId);
      setSnack({
        severity: "success",
        text: `Reboot dispatched to ${res.count} device${res.count === 1 ? "" : "s"}.`,
        withLink: !!res.rolloutId,
      });
    } catch {
      setSnack({ severity: "error", text: "Failed to reboot the group." });
    }
  }

  async function sendMessage(
    g: DeviceGroup,
    v: { title: string; body: string; durationMs: number }
  ) {
    try {
      const res = await foyerApi.messageGroup({
        id: g.groupId,
        title: v.title || undefined,
        body: v.body,
        durationMs: v.durationMs,
      });
      setMessaging(null);
      setSnack({
        severity: "success",
        text: `Message sent to ${res.count} device${res.count === 1 ? "" : "s"}.`,
        withLink: !!res.rolloutId,
      });
    } catch {
      setSnack({ severity: "error", text: "Failed to send the message." });
    }
  }

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Targeting"
        title="Device groups"
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditing(null);
              setDialogOpen(true);
            }}
          >
            New group
          </Button>
        }
      />

      {error && (
        <Typography color="error" variant="body2" sx={{ mt: 2 }}>
          {error}
        </Typography>
      )}

      <Card variant="outlined" sx={{ position: "relative", mt: 3 }}>
        <HelpBadge title="Curated sets of devices you can target in one shot - an OTA rollout, a reboot, or a reception message." />
        <CardContent>
          {!loading && groups.length > 0 && (
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              sx={{ mb: 2, pr: { xs: 0, sm: 4 } }}
            >
              <TextField
                size="small"
                placeholder="Search groups…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                inputProps={{ "aria-label": "Search groups" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ flex: 1, maxWidth: { sm: 360 } }}
              />
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Sort by</InputLabel>
                <Select
                  label="Sort by"
                  value={sort}
                  onChange={(e) => setSort(e.target.value as SortKey)}
                >
                  <MenuItem value="name">Name</MenuItem>
                  <MenuItem value="members">Member count</MenuItem>
                  <MenuItem value="updated">Recently updated</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          )}

          {loading ? (
            <Stack spacing={1.5}>
              {[0, 1, 2].map((i) => (
                <Box
                  key={i}
                  sx={{
                    p: 2,
                    height: 96,
                    borderRadius: 1,
                    bgcolor: tokens.rowAlt,
                    opacity: 0.5,
                  }}
                />
              ))}
            </Stack>
          ) : groups.length === 0 ? (
            <Box sx={{ py: 5, textAlign: "center", color: tokens.inkMuted }}>
              <GroupWorkOutlinedIcon sx={{ fontSize: 40, opacity: 0.5 }} />
              <Typography sx={{ mt: 1 }}>No device groups yet.</Typography>
              <Typography variant="body2">
                Create one to target a curated set of devices in a rollout.
              </Typography>
            </Box>
          ) : visibleGroups.length === 0 ? (
            <Box sx={{ py: 5, textAlign: "center", color: tokens.inkMuted }}>
              <Typography>No groups match “{query}”.</Typography>
            </Box>
          ) : (
            <Stack spacing={1.5}>
              {visibleGroups.map((g) => (
                <GroupCard
                  key={g.groupId}
                  group={g}
                  nameById={nameById}
                  onlineById={onlineById}
                  canReboot={canReboot}
                  canMessage={canMessage}
                  onReboot={() => reboot(g)}
                  onMessage={() => setMessaging(g)}
                  onEdit={() => {
                    setEditing(g);
                    setDialogOpen(true);
                  }}
                  onDelete={() => remove(g)}
                  onRename={(name) => rename(g, name)}
                />
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>

      <GroupDialog
        open={dialogOpen}
        initial={editing}
        devices={devices}
        onClose={() => {
          setDialogOpen(false);
          setEditing(null);
        }}
        onSave={save}
      />

      <MessageDialog
        group={messaging}
        onClose={() => setMessaging(null)}
        onSend={(v) => {
          if (messaging) void sendMessage(messaging, v);
        }}
      />

      <Snackbar
        open={!!snack}
        autoHideDuration={6000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {snack ? (
          <Alert
            severity={snack.severity}
            onClose={() => setSnack(null)}
            action={
              snack.withLink ? (
                <Button
                  color="inherit"
                  size="small"
                  component={RouterLink}
                  to="/ota"
                >
                  View rollout
                </Button>
              ) : undefined
            }
          >
            {snack.text}
          </Alert>
        ) : undefined}
      </Snackbar>
    </PageContainer>
  );
}
